import React, { useState } from "react";
import { Box, Button, Theme, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { AutoTable, NoDataStub } from "@rentablo/components";
import {
  ApiTokenResponse,
  CACHE_KEYS,
  useListApiTokens,
  useRevokeApiToken,
} from "@rentablo/api/apiv1";
import { useMyself } from "@api";
import { ConfirmDeleteDialog } from "@components";
import { SettingsLayout } from "@features/settings/components/SettingsLayout";
import PlusIcon from "@icons/myicons/plus.svg?react";
import { ApiTokenMobileList } from "./ApiTokenMobileList";
import { CreateApiTokenModal } from "./CreateApiTokenModal";
import { useTableConfig } from "./useTableConfig";

export const SettingsApiAccessPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const { data: myself } = useMyself();
  const userId = myself?.id;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<ApiTokenResponse>();
  const [revokeError, setRevokeError] = useState<string>();

  const { data, isPending, isFetching } = useListApiTokens(
    { userId: userId as number },
    { enabled: Boolean(userId) }
  );
  const tokens = data?.apiTokens ?? [];

  const { mutate: revokeToken, isPending: isRevoking } = useRevokeApiToken();

  const handleAskRevoke = (token: ApiTokenResponse) => {
    setRevokeError(undefined);
    setTokenToRevoke(token);
  };

  const handleRevoke = () => {
    if (!userId || tokenToRevoke?.id === undefined) return;
    revokeToken(
      { userId, id: tokenToRevoke.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.LIST_API_TOKENS],
          });
          setTokenToRevoke(undefined);
          enqueueSnackbar(t("settings.apiAccess.snackbar.revoked"), {
            variant: "success",
            autoHideDuration: 3000,
          });
        },
        onError: (err) => {
          setRevokeError(
            err.response?.data?.message ||
              t("settings.apiAccess.deleteModal.error")
          );
        },
      }
    );
  };

  const tableConfig = useTableConfig({ onRevoke: handleAskRevoke });

  const isEmpty = data !== undefined && !tokens.length;

  return (
    <SettingsLayout
      breadcrumbs={[
        { text: t("settings.sidebar.title") },
        { text: t("settings.sidebar.common.apiAccess") },
      ]}
      title={t("settings.apiAccess.title")}
      subtitle={t("settings.apiAccess.subtitle")}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={4}
        mb={4}
      >
        <Typography
          variant="body3"
          color="textSecondary"
          component="p"
          maxWidth={480}
        >
          {t("settings.apiAccess.table.footnote")}
        </Typography>
        <Button
          variant="primary"
          fullWidth={isSm}
          startIcon={<PlusIcon width={17} height={17} />}
          disabled={!userId}
          data-testid="api-token-create-button"
          onClick={() => setIsCreateOpen(true)}
        >
          {t("settings.apiAccess.createButton")}
        </Button>
      </Box>

      {isEmpty ? (
        <NoDataStub
          firstLine={t("settings.apiAccess.emptyState.title")}
          secondLine={t("settings.apiAccess.emptyState.text")}
          height="auto"
          py={10}
        />
      ) : isSm ? (
        <ApiTokenMobileList
          tokens={tokens}
          isPending={isPending}
          onRevoke={handleAskRevoke}
        />
      ) : (
        <AutoTable
          stickyScroll={false}
          settings={tableConfig}
          data={tokens}
          isPending={isPending}
          isFetching={isFetching}
        />
      )}

      {isCreateOpen && userId && (
        <CreateApiTokenModal
          isOpen
          userId={userId}
          onClose={() => setIsCreateOpen(false)}
        />
      )}
      {tokenToRevoke && (
        <ConfirmDeleteDialog
          isOpen
          title={t("settings.apiAccess.deleteModal.title")}
          text={t("settings.apiAccess.deleteModal.text", {
            name: tokenToRevoke.name,
          })}
          errorMessage={revokeError}
          confirmButtonText={t("settings.apiAccess.deleteModal.confirm")}
          cancelButtonText={t("settings.apiAccess.deleteModal.cancel")}
          isDeleteRequestLoading={isRevoking}
          onClose={() => setTokenToRevoke(undefined)}
          onDelete={handleRevoke}
        />
      )}
    </SettingsLayout>
  );
};
